import StorageService from "../services/StorageService";
import {
  getAccessStaffDashboardRoute,
  getDashboardRouteByRole,
  getAvailableRoles,
  clearCurrentRole,
} from "./roleSwitcher";

/**
 * Determines the dashboard route based on user role
 * @returns {string} Dashboard route path
 */
export function getDashboardRoute() {
  const user = StorageService.getUser();

  if (!user) {
    return "/login";
  }

  const accessRoute = getAccessStaffDashboardRoute(user);
  if (accessRoute) {
    // Avoid stale buyer/seller role switcher state for platform staff.
    clearCurrentRole();
    return accessRoute;
  }

  // Try to use role switcher first
  try {
    const roleBasedRoute = getDashboardRouteByRole();
    if (roleBasedRoute) {
      return roleBasedRoute;
    }
  } catch (e) {
    // Fallback to original logic if role switcher fails
    console.warn("Role switcher failed, using fallback logic:", e);
  }

  try {
    const avail = getAvailableRoles();
    if (avail.length === 1 && avail[0]?.id === "logistics") {
      return "/dashboard/logistics";
    }
  } catch {
    /* ignore */
  }

  // user_type_id is stored as comma-separated IDs (e.g., "1,2" or "2" or just 2 as number)
  let userTypeId = user.user_type_id;

  // Handle different formats: number, string, null, undefined
  if (userTypeId === null || userTypeId === undefined) {
    userTypeId = "";
  } else {
    userTypeId = String(userTypeId).trim();
  }

  // Convert to array of IDs (handle comma-separated or single value)
  const typeIds = userTypeId
    ? userTypeId
        .split(",")
        .map((id) => String(id).trim())
        .filter((id) => id && id !== "null" && id !== "undefined")
    : [];

  // Check user_type_id values
  // Mapping: 1 = BUYER, 2 = SELLER, 3 = BOTH
  // If user has multiple roles, prioritize SELLER

  // First, check if user_type_id is a single number (not comma-separated)
  const singleId = parseInt(userTypeId, 10);
  if (!Number.isNaN(singleId) && userTypeId.indexOf(",") === -1) {
    if (singleId === 2) {
      return "/dashboard/seller";
    }
    if (singleId === 1) {
      return "/dashboard/buyer";
    }
    if (singleId === 3) {
      return "/dashboard/seller";
    }
  }

  // Check comma-separated values
  if (typeIds.length > 0) {
    // Check for SELLER first (ID = 2) - highest priority
    const hasSeller = typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === "2" || numId === 2;
    });
    if (hasSeller) {
      return "/dashboard/seller";
    }

    // Check for BUYER (ID = 1)
    const hasBuyer = typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === "1" || numId === 1;
    });
    if (hasBuyer) {
      return "/dashboard/buyer";
    }

    // Check for BOTH (ID = 3) - if both, default to seller
    const hasBoth = typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === "3" || numId === 3;
    });
    if (hasBoth) {
      return "/dashboard/seller";
    }
  }

  // Fallback - check other role fields if available
  const role = user.role || user.company_role || "";
  const roleUpper = String(role).toUpperCase().trim();

  if (roleUpper.includes("SELLER")) {
    return "/dashboard/seller";
  }

  if (roleUpper.includes("BUYER")) {
    return "/dashboard/buyer";
  }

  if (roleUpper.includes("BOTH")) {
    return "/dashboard/seller";
  }

  // Default to buyer dashboard
  return "/dashboard/buyer";
}
